import { z } from 'zod';

import { correlationIdSchema, isoDateTimeSchema, uuidSchema } from '../common/primitives';
import { nodeMetricsSchema } from '../fleet/metrics';
import { nodeStatusSchema } from '../fleet/node-status';
import { preflightReportSchema } from '../fleet/preflight';
import { edgeStatusSchema } from '../networking/edge';
import { blueprintReferenceSchema } from '../provisioning/blueprint';
import { operationStatusSchema, operationTypeSchema } from '../provisioning/operation';
import { siteDesiredStateSchema, siteObservedStateSchema } from '../provisioning/site-state';

/**
 * Ereignisse, die der Master über SSE an angemeldete Clients ausliefert.
 *
 * Die Ereignisse fließen über Redis Pub/Sub, nicht über prozesslokalen Zustand.
 * Das ist keine Vorratsentscheidung: Sobald der Master hinter einem Load
 * Balancer mit mehr als einer Instanz läuft, sähe ein Client sonst nur die
 * Ereignisse der Instanz, mit der er zufällig verbunden ist — ein Fehler, der
 * in der Entwicklung nie auffällt und in Produktion sofort.
 */

export const FLEET_EVENT_TYPES = {
  /** Ein Node hat sich erfolgreich registriert. */
  nodeEnrolled: 'node.enrolled',
  /** Ein Heartbeat ist eingegangen (Livedaten fürs Dashboard). */
  nodeHeartbeat: 'node.heartbeat',
  /** Der Status eines Nodes hat sich geändert (online -> degraded usw.). */
  nodeStatusChanged: 'node.status-changed',
  /** Das Preflight-Ergebnis eines Nodes hat sich verschlechtert oder erholt. */
  nodePreflightChanged: 'node.preflight-changed',
  /** Ein Node wurde administrativ entfernt. */
  nodeRemoved: 'node.removed',
  /** Eine Edge wurde angelegt und wartet auf ihre Registrierung. */
  edgeRegistered: 'edge.registered',
  /** Eine Edge hat sich registriert und holt ab jetzt ihre Konfiguration ab. */
  edgeEnrolled: 'edge.enrolled',
  /** Der Status einer Edge hat sich geändert. */
  edgeStatusChanged: 'edge.status-changed',
  /** Eine Edge wurde entfernt; ihr Token gilt ab sofort nicht mehr. */
  edgeRemoved: 'edge.removed',
  /** Eine Site wurde bestellt und einem Node zugewiesen. */
  siteCreated: 'site.created',
  /** Der beobachtete oder gewünschte Zustand einer Site hat sich geändert. */
  siteStateChanged: 'site.state-changed',
  /** Eine Operation an einer Site hat ihren Status gewechselt. */
  siteOperationChanged: 'site.operation-changed',
  /** Eine Site ist rückstandsfrei entfernt und ihre Reservierung freigegeben. */
  siteDeleted: 'site.deleted',
} as const;

export type FleetEventType = (typeof FLEET_EVENT_TYPES)[keyof typeof FLEET_EVENT_TYPES];

/** Gemeinsamer Rahmen aller Ereignisse. */
const eventEnvelope = {
  /** Eindeutige Ereignis-ID; dient als SSE `id` für Wiederaufnahme. */
  eventId: uuidSchema,
  occurredAt: isoDateTimeSchema,
  correlationId: correlationIdSchema,
} as const;

export const nodeEnrolledEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.nodeEnrolled),
  nodeId: uuidSchema,
  hostname: z.string(),
  region: z.string(),
  generation: z.number().int().positive(),
});

export const nodeHeartbeatEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.nodeHeartbeat),
  nodeId: uuidSchema,
  metrics: nodeMetricsSchema,
  /** `null`, wenn der Agent sein Inventar nicht erheben konnte — nicht `0`. */
  siteCount: z.number().int().nonnegative().nullable(),
});

export const nodeStatusChangedEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.nodeStatusChanged),
  nodeId: uuidSchema,
  hostname: z.string(),
  previousStatus: nodeStatusSchema,
  currentStatus: nodeStatusSchema,
  /** Begründung auf Deutsch, direkt anzeigbar (z. B. "Heartbeat seit 94s überfällig"). */
  reason: z.string(),
});

export const nodePreflightChangedEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.nodePreflightChanged),
  nodeId: uuidSchema,
  report: preflightReportSchema,
});

export const nodeRemovedEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.nodeRemoved),
  nodeId: uuidSchema,
  hostname: z.string(),
});

// -- Edge-Ereignisse ---------------------------------------------------------
//
// Sie tragen bewusst **keine** `nodeId`. Der Ereignisbus filtert Abonnements
// über dieses Feld (`EventBusService.forNode`); ein Client, der einen einzelnen
// Worker beobachtet, soll von Edges nichts hören. Wer sie sehen will, hört den
// ungefilterten Strom ab.

export const edgeRegisteredEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.edgeRegistered),
  edgeId: uuidSchema,
  ordinal: z.number().int().positive(),
  dbmsHostname: z.string(),
});

export const edgeEnrolledEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.edgeEnrolled),
  edgeId: uuidSchema,
  hostname: z.string(),
  publicIpv4: z.string(),
  privateIpv4: z.string(),
});

export const edgeStatusChangedEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.edgeStatusChanged),
  edgeId: uuidSchema,
  previousStatus: edgeStatusSchema,
  currentStatus: edgeStatusSchema,
  /** Begründung auf Deutsch, direkt anzeigbar. */
  reason: z.string(),
});

export const edgeRemovedEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.edgeRemoved),
  edgeId: uuidSchema,
  ordinal: z.number().int().positive(),
});

// -- Site-Ereignisse ---------------------------------------------------------
//
// Jedes Site-Ereignis trägt `nodeId`, obwohl die Site-ID fachlich genügen
// würde. Grund: Der Ereignisbus filtert Abonnements über `event.nodeId`
// (`EventBusService.forNode`). Ein Ereignis ohne dieses Feld wäre für einen auf
// einen Node eingeschränkten Client unsichtbar — und zwar still.

export const siteCreatedEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.siteCreated),
  nodeId: uuidSchema,
  siteId: uuidSchema,
  /** Interner Host-Header, unter dem die Site erreichbar sein wird. */
  hostname: z.string(),
  blueprint: blueprintReferenceSchema,
});

export const siteStateChangedEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.siteStateChanged),
  nodeId: uuidSchema,
  siteId: uuidSchema,
  desiredState: siteDesiredStateSchema,
  previousObservedState: siteObservedStateSchema,
  currentObservedState: siteObservedStateSchema,
  /** Begründung auf Deutsch, direkt anzeigbar. */
  reason: z.string(),
});

export const siteOperationChangedEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.siteOperationChanged),
  nodeId: uuidSchema,
  siteId: uuidSchema,
  operationId: uuidSchema,
  operationType: operationTypeSchema,
  status: operationStatusSchema,
  reason: z.string(),
});

export const siteDeletedEventSchema = z.object({
  ...eventEnvelope,
  type: z.literal(FLEET_EVENT_TYPES.siteDeleted),
  nodeId: uuidSchema,
  siteId: uuidSchema,
  hostname: z.string(),
  reason: z.string(),
});

/**
 * Diskriminierte Union aller Flotten-Ereignisse.
 * Dank `discriminatedUnion` erzwingt TypeScript im Consumer eine vollständige
 * Fallunterscheidung — ein neu hinzugefügter Ereignistyp bricht den Typecheck,
 * statt still ignoriert zu werden.
 */
export const fleetEventSchema = z.discriminatedUnion('type', [
  nodeEnrolledEventSchema,
  nodeHeartbeatEventSchema,
  nodeStatusChangedEventSchema,
  nodePreflightChangedEventSchema,
  nodeRemovedEventSchema,
  edgeRegisteredEventSchema,
  edgeEnrolledEventSchema,
  edgeStatusChangedEventSchema,
  edgeRemovedEventSchema,
  siteCreatedEventSchema,
  siteStateChangedEventSchema,
  siteOperationChangedEventSchema,
  siteDeletedEventSchema,
]);

export type FleetEvent = z.infer<typeof fleetEventSchema>;
export type NodeEnrolledEvent = z.infer<typeof nodeEnrolledEventSchema>;
export type NodeHeartbeatEvent = z.infer<typeof nodeHeartbeatEventSchema>;
export type NodeStatusChangedEvent = z.infer<typeof nodeStatusChangedEventSchema>;
export type NodePreflightChangedEvent = z.infer<typeof nodePreflightChangedEventSchema>;
export type NodeRemovedEvent = z.infer<typeof nodeRemovedEventSchema>;
export type EdgeRegisteredEvent = z.infer<typeof edgeRegisteredEventSchema>;
export type EdgeEnrolledEvent = z.infer<typeof edgeEnrolledEventSchema>;
export type EdgeStatusChangedEvent = z.infer<typeof edgeStatusChangedEventSchema>;
export type EdgeRemovedEvent = z.infer<typeof edgeRemovedEventSchema>;
export type SiteCreatedEvent = z.infer<typeof siteCreatedEventSchema>;
export type SiteStateChangedEvent = z.infer<typeof siteStateChangedEventSchema>;
export type SiteOperationChangedEvent = z.infer<typeof siteOperationChangedEventSchema>;
export type SiteDeletedEvent = z.infer<typeof siteDeletedEventSchema>;

/**
 * Der Node, zu dem ein Ereignis gehört — oder `null`, wenn es keinen gibt.
 *
 * Seit M4b trägt **nicht mehr jedes** Ereignis eine `nodeId`: Die Edge ist kein
 * Worker. Ein direkter Zugriff auf `event.nodeId` wäre über die Union hinweg
 * nicht mehr typsicher, und ein Abonnement auf einen einzelnen Node bekäme
 * Ereignisse zu sehen, die es nichts angehen.
 *
 * Als Funktion im Vertrag und nicht als Prüfung beim Verbraucher: Beide Seiten
 * des Ereignisbusses filtern danach, und zwei Auslegungen davon, was „gehört zu
 * diesem Node" heißt, wären zwei Gelegenheiten für ein stilles Leck.
 */
export function eventNodeId(event: FleetEvent): string | null {
  return 'nodeId' in event ? event.nodeId : null;
}

/** Redis-Pub/Sub-Kanal, über den alle Flotten-Ereignisse laufen. */
export const FLEET_EVENT_CHANNEL = 'kraaapf:events:fleet';
